/**
 * The suite to open first on the eval pages, kept in this browser only. Pages use it when the
 * URL names no suite; a suite in the URL always wins, so shared links still work.
 */
import { useCallback, useSyncExternalStore } from 'react';

const STORAGE_KEY = 'evals.defaultSuite';
// Tells other hooks on this page; the `storage` event only reaches other tabs.
const CHANGE_EVENT = 'evals-default-suite-change';

function read(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

function subscribe(onChange: () => void): () => void {
  const onStorage = (event: StorageEvent) => {
    if (event.key === STORAGE_KEY) onChange();
  };
  window.addEventListener('storage', onStorage);
  window.addEventListener(CHANGE_EVENT, onChange);
  return () => {
    window.removeEventListener('storage', onStorage);
    window.removeEventListener(CHANGE_EVENT, onChange);
  };
}

export function useDefaultSuite(): [string | null, (suiteId: string | null) => void] {
  const defaultSuiteId = useSyncExternalStore(subscribe, read, () => null);

  const setDefaultSuiteId = useCallback((suiteId: string | null) => {
    try {
      if (suiteId === null) localStorage.removeItem(STORAGE_KEY);
      else localStorage.setItem(STORAGE_KEY, suiteId);
    } catch {
      // Storage is blocked: the default stays unset.
    }
    window.dispatchEvent(new Event(CHANGE_EVENT));
  }, []);

  return [defaultSuiteId, setDefaultSuiteId];
}
